import React, { type Dispatch, type SetStateAction, useEffect,useState } from 'react'
import { type SupportedLocale } from '@layerfi/components'
import { NavLink, useLocation } from 'react-router-dom'

import './styles.scss'

import { useSidebarContext } from '../../contexts/SidebarContext'
import AccountingIcon from '../../icons/Accounting'
import BankIcon from '../../icons/Bank'
import ChevronRightIcon from '../../icons/ChevronRight'
import FileTextIcon from '../../icons/FileText'
import ShoppingBagIcon from '../../icons/ShoppingBag'
import UserIcon from '../../icons/User'
import { useSizeClass } from '../../utils/useWindowSize'
import { DemoControls } from '../DemoControls'
import { getActiveSegment,getBaseUrlFromPathName } from '../Nav/navUtils'
import { useNavigationLinks } from '../Nav/useNavigationLinks'

interface SidebarProps {
  demoName: string
  brandLogoUrl: string
  setImportingTransactions: Dispatch<SetStateAction<boolean>>
  isMobileView: boolean
  setIsMobileView: (value: boolean) => void
  isBookkeepingMode: boolean
  setBookkeepingMode: (value: boolean) => void
  showInvoicesTab: boolean
  showTooltips: boolean
  setShowTooltips: Dispatch<SetStateAction<boolean>>
  isSidebarHidden?: boolean
  locale: SupportedLocale
  setLocale: Dispatch<SetStateAction<SupportedLocale>>
  forceEnableTimeTracking?: boolean
  useSolopreneurLayout?: boolean
  enableTimeTracking?: boolean
}

export const Sidebar = ({
  demoName,
  brandLogoUrl,
  setImportingTransactions,
  isMobileView,
  setIsMobileView,
  setBookkeepingMode,
  isBookkeepingMode,
  showInvoicesTab,
  showTooltips,
  setShowTooltips,
  isSidebarHidden,
  locale,
  setLocale,
  forceEnableTimeTracking = false,
  useSolopreneurLayout = false,
  enableTimeTracking = false,
}: SidebarProps) => {
  const { isSidebarOpen, openSidebar } = useSidebarContext()
  const { isMobile } = useSizeClass()
  const [isOpen, setIsOpen] = useState(false)
  const location = useLocation()
  const baseUrl = getBaseUrlFromPathName(location.pathname)
  const activeSegment = getActiveSegment(location.pathname)

  useEffect(() => {
    const mainContent = document.querySelector('section.main-content') as HTMLElement
    const body = document.body as HTMLElement
    if (!mainContent) return

    if (isSidebarOpen) {
      const scrollY = mainContent.scrollTop
      mainContent.style.setProperty('--scroll-y', `${scrollY}px`)
      mainContent.classList.add('sidebar-modal-open')
      body.classList.add('sidebar-modal-open')
    } else {
      const scrollY = mainContent.style.getPropertyValue('--scroll-y')
      mainContent.classList.remove('sidebar-modal-open')
      body.classList.remove('sidebar-modal-open')
      mainContent.scrollTo(0, parseInt(scrollY || '0'))
    }

    return () => {
      if (mainContent) {
        mainContent.classList.remove('sidebar-modal-open')
        body.classList.remove('sidebar-modal-open')
        mainContent.scrollTo(0, 0)
      }
    }
  }, [isSidebarOpen])

  const allAccountingLinks = useNavigationLinks({
    forceEnableTimeTracking,
  })

  const isAccountingActive = allAccountingLinks.some(
    link => link.to === activeSegment,
  )

  const [isAccountingOpen, setIsAccountingOpen] = useState(
    isMobile && isAccountingActive,
  )

  useEffect(() => {
    if (isMobile && isAccountingActive) {
      setIsAccountingOpen(true)
    }
  }, [isMobile, isAccountingActive])

  const toggleAccordion = () => {
    setIsOpen(!isOpen)
  }

  const toggleAccounting = () => {
    setIsAccountingOpen(!isAccountingOpen)
  }

  return (
    <section className='sidebar'>
      <div
        className={`sidebar-bg ${isOpen ? 'sidebar-bg-open' : ''}`}
        onClick={toggleAccordion}
      />
      <div className='sidebar-content'>
        <div className='sidebar-header'>
          <img
            className='brand-logo'
            src={brandLogoUrl}
            alt='Client Logo'
          />
          <button className='sidebar-close-btn' onClick={() => openSidebar(false)}>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 18 18'
              fill='none'
            >
              <path
                d='M13.5 4.5L4.5 13.5'
                stroke='black'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M4.5 4.5L13.5 13.5'
                stroke='black'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        </div>
        <ul>
          {isSidebarHidden ? (
            allAccountingLinks.map(link => (
              <li key={link.to}>
                <NavLink
                  to={`${baseUrl}/${link.to}`}
                  className={`nav-link ${
                    activeSegment === link.to ? 'active' : ''
                  }`}
                  onClick={() => openSidebar(false)}
                >
                  {link.label}
                </NavLink>
              </li>
            ))
          ) : (
            <>
              <li>
                {showInvoicesTab ? (
                  <NavLink
                    to={`${baseUrl}/invoices`}
                    className='nav-link active'
                    onClick={() => openSidebar(false)}
                  >
                    <FileTextIcon />
                    Invoices
                  </NavLink>
                ) : (
                  <span className='nav-link disabled'>
                    <FileTextIcon /> Invoices
                  </span>
                )}
              </li>
              <span className='nav-link disabled'>
                <UserIcon /> Customers
              </span>
              <span className='nav-link disabled'>
                <ShoppingBagIcon /> Catalog
              </span>
              <li>
                {isMobile ? (
                  <>
                    <button
                      className={`nav-link nav-link-button ${
                        isAccountingActive ? 'active' : ''
                      } ${isAccountingOpen ? 'expanded' : ''}`}
                      onClick={toggleAccounting}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <AccountingIcon />
                        Accounting
                      </div>
                      <ChevronRightIcon
                        className={`sidebar-chevron ${
                          isAccountingOpen ? 'sidebar-chevron--open' : ''
                        }`}
                      />
                    </button>
                    {isAccountingOpen && (
                      <ul className='sidebar-submenu'>
                        {allAccountingLinks.map(link => (
                          <li key={link.to}>
                            <NavLink
                              to={`${baseUrl}/${link.to}`}
                              className={`nav-link nav-link--sub ${
                                activeSegment === link.to ? 'active' : ''
                              }`}
                              onClick={() => {
                                openSidebar(false)
                                setIsAccountingOpen(false)
                              }}
                            >
                              {link.label}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <NavLink
                    to={baseUrl}
                    className={`nav-link ${isAccountingActive ? 'active' : ''}`}
                    onClick={() => openSidebar(false)}
                  >
                    <AccountingIcon />
                    Accounting
                  </NavLink>
                )}
              </li>
              <span className='nav-link disabled'>
                <BankIcon /> Banking
              </span>
            </>
          )}
        </ul>
      </div>
      {!isSidebarHidden && (
        <DemoControls
          demoName={demoName}
          setImportingTransactions={setImportingTransactions}
          isMobileView={isMobileView}
          setIsMobileView={setIsMobileView}
          isBookkeepingMode={isBookkeepingMode}
          setBookkeepingMode={setBookkeepingMode}
          showTooltips={showTooltips}
          setShowTooltips={setShowTooltips}
          locale={locale}
          setLocale={setLocale}
          useSolopreneurLayout={useSolopreneurLayout}
          enableTimeTracking={enableTimeTracking}
        />
      )}
    </section>
  )
}
